import { Channel } from 'pusher-js'
import { useCallback, useEffect, useState } from 'react'

import { EventHandlers } from '@bonhams1793/bonhams-typescript'

import { usePusherInstance } from './Context'

export type BonhamsChannel =
  | `user-${string}-v${number}`
  | `cache-lot-${string}-v${number}`
  | `auction-${string}-v${number}`

export const usePusher = () => {
  const pusherInstance = usePusherInstance()
  const [pusherConnectionStatus, setPusherConnectionStatus] = useState<
    'WAITING' | 'CONNECTED' | 'DISCONNECTED'
  >('WAITING')

  useEffect(() => {
    const stateChange = ({
      current,
    }: {
      previous: string
      current: string
    }) => {
      setPusherConnectionStatus(
        current === 'connected' ? 'CONNECTED' : 'DISCONNECTED'
      )
    }

    const failed = () => {
      setPusherConnectionStatus('DISCONNECTED')
    }

    pusherInstance?.connection.bind('state_change', stateChange)
    pusherInstance?.connection.bind('failed', failed)

    return () => {
      pusherInstance?.connection.unbind('state_change', stateChange)
      pusherInstance?.connection.unbind('failed', failed)
    }
  }, [pusherInstance])

  const subscribe = useCallback(
    (
      channelName: BonhamsChannel,
      events?: EventHandlers,
      onSubscribed?: () => void
    ) => {
      if (!pusherInstance) return

      let channel: Channel | undefined = pusherInstance.channel(channelName)

      if (channel && channel.subscribed) return channel

      channel = pusherInstance.subscribe(channelName)

      channel.bind('pusher:subscription_succeeded', () => {
        onSubscribed?.()
      })

      if (events) {
        for (const [eventName, callback] of Object.entries(events)) {
          channel.bind(eventName, callback)
        }
      }
    },
    [pusherInstance]
  )

  const unsubscribe = useCallback(
    (channelName: BonhamsChannel) => {
      if (!pusherInstance) return

      pusherInstance.channel(channelName)?.unbind_all()
      pusherInstance.unsubscribe(channelName)
    },
    [pusherInstance]
  )

  return {
    pusherConnectionStatus,
    subscribe,
    unsubscribe,
  }
}
